<template>
  <div class="contents">
    <div class="layout">
      <!-- s::title-breadcrumbs -->
      <div class="page-top">
        <h2>{{ getMenuLabel() }} 관리</h2>
        <div class="breadcrumbs">
          <span>컨텐츠 관리</span>
          <span>{{ getMenuLabel() }} 관리</span>
          <span class="color-black">{{ articleUuid ? item.title : '등록' }}</span>
        </div>
      </div>
      <!-- e::title-breadcrumbs -->

      <div class="white-panel mt30">
        <div class="table-wrap">
          <table class="table-type2">
            <colgroup>
              <col style="width:150px;">
              <col>
            </colgroup>
            <tbody>
            <tr v-if="articleType === 'news'">
              <th>종류<em class="required">*</em></th>
              <td>
                <select v-model="item.type">
                  <option value="" disabled>선택</option>
                  <option v-for="type in ARTICLE_NEWS_TYPE" :value="type.value">{{ type.label }}</option>
                </select>
              </td>
            </tr>
            <tr>
              <th>제목<em class="required">*</em></th>
              <td>
                <input type="text" v-model="item.title"/>
              </td>
            </tr>
            <tr>
              <th>시작일<em class="required">*</em></th>
              <td>
                <Datepicker inputmode="numeric" v-model="item.periodFrom" :enable-time-picker="false" :format="'yyyy-MM-dd'"
                            v-mask="'####-##-##'" :text-input="{format: 'yyyy-MM-dd'}" placeholder="YYYY-MM-DD" auto-apply/>
              </td>
            </tr>
            <tr>
              <th>종료일<em class="required">*</em></th>
              <td>
                <Datepicker inputmode="numeric" v-model="item.periodTo" :enable-time-picker="false" :format="'yyyy-MM-dd'"
                            v-mask="'####-##-##'" :text-input="{format: 'yyyy-MM-dd'}" placeholder="YYYY-MM-DD" auto-apply/>
              </td>
            </tr>
            <tr>
              <th>날짜 추가 설명</th>
              <td>
                <input type="text" maxlength="255" v-model="item.periodMemo"/>
              </td>
            </tr>
            <tr v-if="articleType === 'news'">
              <th>주최/주관</th>
              <td>
                <input type="text" maxlength="255" v-model="item.organizer"/>
              </td>
            </tr>
            <tr>
              <th>시작시간</th>
              <td>
                <input type="text" inputmode="numeric" v-model="item.timeFrom" v-mask="'##:##'" placeholder="HH:mm"/>
              </td>
            </tr>
            <tr>
              <th>종료시간</th>
              <td>
                <input type="text" inputmode="numeric" v-model="item.timeTo" v-mask="'##:##'" placeholder="HH:mm"/>
              </td>
            </tr>
            <tr>
              <th>시간 추가 설명</th>
              <td>
                <input type="text" maxlength="255" v-model="item.timeMemo"/>
              </td>
            </tr>
            <tr>
              <th>
                <a @click="openKakaoMap(item.location)" style="cursor: pointer;"> 장소
                  <img src="@/assets/images/icon/location-dot-solid.svg" alt="카카오맵"
                       style="width: clamp(16px, 1.4vw, 18px); margin-bottom: -4px; margin-left: 8px;"/>
                </a>
              </th>
              <td>
                <div style="position: relative;">
                  <input
                      type="text"
                      v-model="item.location"
                      autocomplete="off"
                      @focus="onLocationFocus"
                      @input="onLocationInput"
                      @blur="onLocationBlur"
                  />
                  <ul
                      v-if="articleType === 'exhibitions' && showLocationSuggest && locationMatches.length"
                      class="loc-suggest"
                  >
                    <li
                        v-for="(loc, idx) in locationMatches"
                        :key="idx"
                        class="loc-suggest__item"
                        @mousedown.prevent="applyLocation(loc)"
                    >
                      {{ loc.location }}
                      <span v-if="loc.locationDetail" style="color:#999;">— {{ loc.locationDetail }}</span>
                    </li>
                  </ul>
                </div>
                <p v-if="articleType === 'exhibitions'" style="font-size: 12px; color: #999; margin-top: 4px;">
                  기존 장소명을 입력해 목록에서 선택하면 날짜/시간 추가 설명·시작·종료 시간·장소 상세가 자동으로 채워집니다.
                </p>
              </td>
            </tr>
            <tr>
              <th>장소 상세</th>
              <td>
                <input type="text" v-model="item.locationDetail"/>
              </td>
            </tr>
            <tr v-if="articleType === 'exhibitions'">
              <th>지도 좌표</th>
              <td>
                <div class="coord-inputs">
                  <label for="coord-lat">위도</label>
                  <input id="coord-lat" type="text" inputmode="decimal" v-model="item.latitude" placeholder="37.5796000" autocomplete="off"/>
                  <label for="coord-lng">경도</label>
                  <input id="coord-lng" type="text" inputmode="decimal" v-model="item.longitude" placeholder="126.9770000" autocomplete="off"/>
                  <span class="coord-badge" :class="hasCoordinates ? 'coord-badge--ok' : 'coord-badge--none'">
                    {{ hasCoordinates ? '지도에 표시됨' : '좌표 미확보' }}
                  </span>
                </div>
                <div class="coord-actions">
                  <button type="button" class="coord-actions__pick" @click="openCoordinatePicker">지도에서 지정</button>
                  <button type="button" @click="prefillCoordinatesFromLocation" :disabled="!item.location">기존 장소 좌표 불러오기</button>
                  <button type="button" @click="openKakaoMapAtCoordinates" :disabled="!hasCoordinates">지도에서 확인</button>
                  <button type="button" @click="clearCoordinateInputs" :disabled="!item.latitude && !item.longitude">좌표 지우기</button>
                  <button type="button" class="coord-actions__bulk" @click="applyCoordinatesToLocation"
                          :disabled="!hasCoordinates || !item.location">이 장소 전체에 적용</button>
                </div>
                <p class="coord-hint">
                  좌표는 지도 화면에만 쓰입니다. 비워둬도 저장되며, 그 전시는 지도에 표시되지 않습니다.
                  <strong>지도에서 지정</strong>으로 장소를 검색해 좌표를 찍을 수 있고,
                  장소를 목록에서 선택하면 같은 장소의 좌표가 자동으로 채워집니다.
                  <br/>
                  <strong>이 장소 전체에 적용</strong>은 장소명이 같은 다른 전시의 <em>좌표 미확보 건만</em> 채웁니다(이미 좌표가 있는 건은 그대로 둡니다).
                </p>
              </td>
            </tr>
            <tr>
              <th>내용<em class="required">*</em></th>
              <td>
                <MimioRichEditor v-model="item.content" :upload-fn="uploadImage"/>
              </td>
            </tr>
            <tr>
              <th>공식 URL<a :href="item.originLink" target="_blank" class="link" rel="noopener noreferrer" >🔗</a></th>
              <td>
                <input type="text" maxlength="255" v-model="item.originLink"/>
              </td>
            </tr>
            <tr>
              <th>연락처</th>
              <td>
                <input type="text" v-model="item.contactPhoneNum"/>  <!--  v-mask="'###-####-####'" -->
              </td>
            </tr>
            <tr>
              <th>이메일</th>
              <td>
                <input type="text" v-model="item.contactEmail"/>
              </td>
            </tr>
            <tr>
              <th style="vertical-align: top;">프리뷰 이미지</th>
              <td>
                <div class="file-list">
                  <ul>
                    <li>
                      <div class="attachment">
                        <div class="filebox2">
                          <label for="ex_file1">
                            <span>{{ item.previewImageUrl }}</span>
                          </label>
                          <input type="file" id="ex_file1" @change="selectPreviewImage" accept="image/*"/>
                        </div>
                      </div>
                    </li>
                  </ul>
                  <img v-if="item.previewImageUrl" :src="item.previewImageUrl" style="width: 287px; aspect-ratio: 287.73 / 394.6;" alt=""/>
                  <button v-if="item.previewImageUrl" @click="deleteFile" type="button">
                    <img src="@/assets/images/icon/ico_close_gray.svg"
                         style="width: 40px; padding-left: 5px" alt="" />
                  </button>
                  <p v-else>미등록 시 메인 이미지로 대체</p>
                </div>
              </td>
            </tr>
            <tr>
              <th style="vertical-align: top;">메인 이미지<em class="required">*</em></th>
              <td>
                <div class="file-list">
                  <ul>
                    <li>
                      <div class="attachment">
                        <div class="filebox2">
                          <label for="ex_file2">
                            <span>{{ item.imageUrl }}</span>
                          </label>
                          <input type="file" id="ex_file2" @change="selectImage" accept="image/*"/>
                        </div>
                      </div>
                    </li>
                  </ul>
                  <img v-if="item.imageUrl" :src="item.imageUrl" style="width: 287px; aspect-ratio: 287.73 / 394.6;" alt=""/>
                </div>
              </td>
            </tr>
            <tr>
              <th>공개여부<em class="required">*</em></th>
              <td>
                <select v-model="item.isHidden">
                  <option value="false">공개</option>
                  <option value="true">비공개</option>
                </select>
              </td>
            </tr>
            <tr>
              <th>Home 전시<em class="required">*</em></th>
              <td>
                <select v-model="item.isHomeVisible">
                  <option value="true">공개</option>
                  <option value="false">비공개</option>
                </select>
              </td>
            </tr>
            <tr v-if="articleUuid">
              <th>생성자</th>
              <td>
                <input type="text" :value="item.createdBy" disabled/>
              </td>
            </tr>
            <tr v-if="articleUuid">
              <th>생성일시</th>
              <td>
                <input type="text" :value="item.createdAt" disabled/>
              </td>
            </tr>
            <tr v-if="articleUuid">
              <th>최종 수정자</th>
              <td>
                <input type="text" :value="item.updatedBy" disabled/>
              </td>
            </tr>
            <tr v-if="articleUuid">
              <th>최종 수정일시</th>
              <td>
                <input type="text" :value="item.updatedAt" disabled/>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="button-panel mt30">
        <div class="left">
          <router-link :to="{ name: 'Article', params: { articleType: articleType }}" class="button white-button round-button gray2">목록</router-link>
        </div>
        <div class="right">
          <button @click="saveItem" type="button" class="button navy-button">{{articleUuid ? '수정하기' : '등록하기'}}</button>
        </div>
      </div>
    </div>

    <ImageCropper :imageFile="imageFile" :maxWidth="maxWidth" :maxHeight="maxHeight" :isFixedSize="isFixedSize"
                  @cropped="handleCroppedImage"/>

    <ExhibitionCoordinatePicker
        v-if="showCoordinatePicker"
        :initial-query="item.location || ''"
        :initial-lat="item.latitude"
        :initial-lng="item.longitude"
        @picked="applyPickedCoordinates"
        @close="showCoordinatePicker = false"
    />
  </div>
</template>

<script setup>
import Datepicker from '@vuepic/vue-datepicker';
import {computed, onMounted, ref} from 'vue';
import {apiClient} from '@/services/auth-header';
import {useRoute} from 'vue-router';
import {format} from 'date-fns';
import {ARTICLE_NEWS_TYPE} from '@/common/common-enum';
import ImageCropper from '@/components/ui/ImageCropper.vue';
import MimioRichEditor from '@/components/common/MimioRichEditor.vue';
import ExhibitionCoordinatePicker from '@/components/contents/ExhibitionCoordinatePicker.vue';

const route = useRoute();
const articleUuid = ref(route.params.uuid);
const articleType = ref(route.params.articleType);

const item = ref({
  uuid: null,
  type: null, // 뉴스에서만 사용
  title: null,
  content: null,
  periodFrom: null,
  periodTo: null,
  periodMemo: null,
  organizer: null, // 뉴스에서만 사용
  timeFrom: null,
  timeTo: null,
  timeMemo: null,
  location: null,
  locationDetail: null,
  latitude: null,   // 전시에서만 사용 (mimio#32)
  longitude: null,
  originLink: null,
  contactPhoneNum: null,
  contactEmail: null,
  previewImageUrl: null,
  imageUrl: null,
  isHidden: false,
  isHomeVisible: false,
  createdAt: null,
  createdBy: null,
  updatedAt: null,
  updatedBy: null,
})

const selectedImage = ref(null);
const selectedPreviewImage = ref(null);
const API_URL = '/contents';

// 장소 불러오기 (전시): 기존 전시 장소 + 연관 정보 자동완성
const locationOptions = ref([]);
const showLocationSuggest = ref(false);

const fetchLocationOptions = async () => {
  try {
    const response = await apiClient.get(API_URL + '/articles/exhibitions/locations');
    locationOptions.value = response.data; // [{location, locationDetail, periodMemo, timeFrom, timeTo, timeMemo}]
  } catch (error) {
    console.error('장소 목록 조회 실패:', error);
  }
};

const locationMatches = computed(() => {
  const q = (item.value.location || '').trim().toLowerCase();
  const list = q
      ? locationOptions.value.filter((l) => (l.location || '').toLowerCase().includes(q))
      : locationOptions.value;
  return list.slice(0, 20);
});

const onLocationFocus = () => {
  showLocationSuggest.value = true;
};
const onLocationInput = () => {
  showLocationSuggest.value = true;
};
const onLocationBlur = () => {
  // 항목 클릭(mousedown)이 먼저 처리되도록 지연 후 닫기
  setTimeout(() => {
    showLocationSuggest.value = false;
  }, 150);
};

const applyLocation = (loc) => {
  item.value.location = loc.location;
  item.value.locationDetail = loc.locationDetail;
  item.value.periodMemo = loc.periodMemo;
  item.value.timeFrom = loc.timeFrom;
  item.value.timeTo = loc.timeTo;
  item.value.timeMemo = loc.timeMemo;
  // 좌표는 이미 입력된 값을 덮지 않는다 — 담당자가 방금 찍어둔 좌표를 목록 선택이 지우면 안 된다
  if (loc.latitude != null && loc.longitude != null && !hasCoordinates.value) {
    item.value.latitude = loc.latitude;
    item.value.longitude = loc.longitude;
  }
  showLocationSuggest.value = false;
};

/*
 * 좌표 (mimio#32 3a)
 *
 * 저장된 장소명 쪽 정규화 키는 서버가 locationKey 로 내려준다. 여기서는 담당자가 방금 입력한 문자열만 같은 규칙으로 접는다
 * — 서버(LocationNameNormalizer)와 같은 3단계여야 한다: NFKC → 공백류 제거 → 소문자.
 */
const normalizeLocationName = (raw) => {
  if (!raw) {
    return null;
  }
  const key = raw.normalize('NFKC').replace(/[\s\u00A0\u200B]+/g, '').toLowerCase();
  return key === '' ? null : key;
};

const KOREA_LAT_RANGE = [33.0, 39.5];
const KOREA_LNG_RANGE = [124.0, 132.0];

// 입력값 → 숫자. 빈 칸은 null (빈 문자열을 그대로 보내면 서버 파싱이 갈린다)
const parseCoordinate = (raw) => {
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return null;
  }
  const num = Number(String(raw).trim());
  return Number.isFinite(num) ? num : NaN;
};

const hasCoordinates = computed(() => {
  const lat = parseCoordinate(item.value.latitude);
  const lng = parseCoordinate(item.value.longitude);
  return lat !== null && lng !== null && !Number.isNaN(lat) && !Number.isNaN(lng);
});

// 조회 시점에 좌표가 있었는지. 저장할 때 "비운 것"과 "원래 없던 것"을 구분해 clearCoordinates 를 정한다
const loadedWithCoordinates = ref(false);

const findLocationOptionForCurrentInput = () => {
  const key = normalizeLocationName(item.value.location);
  if (!key) {
    return null;
  }
  return locationOptions.value.find(
      (loc) => (loc.locationKey || normalizeLocationName(loc.location)) === key
          && loc.latitude != null && loc.longitude != null
  ) || null;
};

const prefillCoordinatesFromLocation = () => {
  const match = findLocationOptionForCurrentInput();
  if (!match) {
    alert('같은 장소명으로 저장된 좌표가 없습니다. 지도에서 직접 지정해 주세요.');
    return;
  }
  item.value.latitude = match.latitude;
  item.value.longitude = match.longitude;
};

const clearCoordinateInputs = () => {
  item.value.latitude = null;
  item.value.longitude = null;
};

// 지도에서 지정 (3b) — 카카오 장소 검색 + 핀 찍기
const showCoordinatePicker = ref(false);
const openCoordinatePicker = () => {
  showCoordinatePicker.value = true;
};
const applyPickedCoordinates = ({latitude, longitude}) => {
  // 소수 7자리 = DECIMAL(10,7) 과 같은 정밀도. 지도가 주는 15자리를 그대로 넣으면 저장 시 잘려 화면과 DB 가 어긋난다
  item.value.latitude = Number(latitude).toFixed(7);
  item.value.longitude = Number(longitude).toFixed(7);
  showCoordinatePicker.value = false;
};

const openKakaoMapAtCoordinates = () => {
  if (!hasCoordinates.value) {
    return;
  }
  const lat = parseCoordinate(item.value.latitude);
  const lng = parseCoordinate(item.value.longitude);
  // 좌표를 지도에서 눈으로 확인하는 용도 — 링크라 카카오 도메인 등록과 무관하게 동작한다
  window.open(`https://map.kakao.com/link/map/${encodeURIComponent(item.value.location || '전시장소')},${lat},${lng}`, '_blank');
};

const applyCoordinatesToLocation = async () => {
  if (!validateCoordinates()) {
    return;
  }
  if (!confirm(`장소명이 "${item.value.location}" 인 다른 전시의 좌표 미확보 건에도 이 좌표를 채웁니다. 진행할까요?`)) {
    return;
  }
  try {
    const {data} = await apiClient.put(API_URL + '/articles/exhibitions/locations/coordinates', {
      location: item.value.location,
      latitude: parseCoordinate(item.value.latitude),
      longitude: parseCoordinate(item.value.longitude),
      overwriteExisting: false,
    });
    alert(`대상 ${data.matchedCount}건 중 ${data.updatedCount}건에 좌표를 채웠습니다.`
        + (data.skippedExistingCount ? ` (이미 좌표가 있어 건너뜀 ${data.skippedExistingCount}건)` : ''));
    await fetchLocationOptions();
  } catch (error) {
    console.error('좌표 일괄 적용 실패:', error);
    alert('좌표 일괄 적용 중 오류가 발생했습니다.');
  }
};

// 위경도 뒤바뀜·오타를 저장 전에 잡는다. 둘 다 비어 있는 것은 정상(좌표 없이 저장 가능)
const validateCoordinates = () => {
  const lat = parseCoordinate(item.value.latitude);
  const lng = parseCoordinate(item.value.longitude);

  if (lat === null && lng === null) {
    return true;
  }
  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    alert('위도·경도는 숫자로 입력해 주세요.');
    return false;
  }
  if (lat === null || lng === null) {
    alert('위도와 경도는 함께 입력해야 합니다. 한쪽만 있으면 지도에 표시할 수 없습니다.');
    return false;
  }
  if (lat < KOREA_LAT_RANGE[0] || lat > KOREA_LAT_RANGE[1]) {
    alert(`위도는 ${KOREA_LAT_RANGE[0]} ~ ${KOREA_LAT_RANGE[1]} 범위여야 합니다. 경도를 잘못 넣지 않았는지 확인해 주세요.`);
    return false;
  }
  if (lng < KOREA_LNG_RANGE[0] || lng > KOREA_LNG_RANGE[1]) {
    alert(`경도는 ${KOREA_LNG_RANGE[0]} ~ ${KOREA_LNG_RANGE[1]} 범위여야 합니다. 위도를 잘못 넣지 않았는지 확인해 주세요.`);
    return false;
  }
  return true;
};

const getMenuLabel = () => {
  if (articleType.value === 'exhibitions') {
    return '전시';
  } else if (articleType.value === 'events') {
    return '이벤트'
  }
  return '뉴스'
}

const deleteFile = () => {
  if (confirm('삭제 하시겠습니까?')) {
    selectedPreviewImage.value = null;
    item.value.previewImageUrl = null;
  }
};

const openKakaoMap = (address) => {
  if (!address) {
    return;
  }
  const encodedAddress = encodeURIComponent(address);
  const kakaoMapUrl = `https://map.kakao.com/?q=${encodedAddress}`;
  window.open(kakaoMapUrl, '_blank');
};

const fetchItem = async () => {
  try {
    const response = await apiClient.get(API_URL + `/articles/${articleType.value}/${articleUuid.value}`);
    item.value = response.data;
    loadedWithCoordinates.value = response.data.latitude != null && response.data.longitude != null;
  } catch (error) {
    alert(error.message);
  }
};

const selectImage = (event) => {
  selectedImage.value = event.target.files[0];
  item.value.imageUrl = URL.createObjectURL(event.target.files[0]);
};

// const selectPreviewImage = (event) => {
//   selectedPreviewImage.value = event.target.files[0];
//   item.value.previewImageUrl = URL.createObjectURL(event.target.files[0]);
// };

const imageFile = ref(null);
const maxWidth = 287.73 * 1.2;
const maxHeight = 394.6 * 1.2;
let isFixedSize = true; // 이미지 크기 고정 여부(고정 하면 maxWidth, maxHeight size 로 파일 생성, 안하면 비율만 유지)

const selectPreviewImage = async (event) => {
  const fileInput = event.target; // file input element
  const file = fileInput.files[0];
  if (!file) {
    fileInput.value = '';
    return;
  }
  // const maxFileSize = 10 * 1024 * 1024; // 10MB
  // if (file.size > maxFileSize) {
  //   alert("파일 크기가 너무 큽니다. 10MB 이하의 파일만 업로드 가능합니다.");
  //   return;  // 용량 초과시 함수 종료
  // }

  // 부모에서 이미지 파일만 전달
  imageFile.value = file;
  fileInput.value = '';
};

const handleCroppedImage = async (newVal) => {
  const {file, url, name} = newVal;
  item.value.previewImageUrl = url;
  selectedPreviewImage.value = file;
};

// 워드형 본문 에디터 인라인 이미지 → S3 업로드 후 URL 반환 (실패 시 에디터가 dataURL fallback)
const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append('file', file);
  const res = await apiClient.post(API_URL + '/articles/content-image', fd, {
    headers: {'Content-Type': 'multipart/form-data'},
  });
  return res.data.url;
};

const saveItem = async () => {
  if (!validateItem()) {
    return;
  }

  const msg = articleUuid.value ? '수정 하시겠습니까?' : '등록 하시겠습니까?';
  if (confirm(msg)) {
    const formData = new FormData();
    if (selectedPreviewImage.value) {
      formData.append('previewImage', selectedPreviewImage.value);
    }
    if (selectedImage.value) {
      formData.append('image', selectedImage.value);
    }

    item.value.periodFrom = format(item.value.periodFrom, 'yyyy-MM-dd');
    item.value.periodTo = format(item.value.periodTo, 'yyyy-MM-dd');

    const payload = {...item.value};
    if (articleType.value === 'exhibitions') {
      const lat = parseCoordinate(item.value.latitude);
      const lng = parseCoordinate(item.value.longitude);
      payload.latitude = lat;
      payload.longitude = lng;
      // 좌표를 비운 경우에만 "지워달라"고 명시한다. 서버는 좌표가 없으면 기존 값을 유지하므로,
      // 이 플래그가 없으면 백필해둔 좌표를 화면에서 비워도 안 지워진다.
      payload.clearCoordinates = loadedWithCoordinates.value && lat === null && lng === null;
    }
    formData.append('reqDto', JSON.stringify(payload));

    try {
      if (!articleUuid.value) {
        // 등록
        await apiClient.post(API_URL + `/articles/${articleType.value}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        // 수정
        await apiClient.put(API_URL + `/articles/${articleType.value}/${articleUuid.value}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      const resultMsg = articleUuid.value ? '수정 되었습니다.' : '등록 되었습니다.';
      alert(resultMsg);
      if (articleUuid.value) {
        await fetchItem();
      } else {
        history.back();
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(error.response.data);
    }
  }
};

const validateItem = () => {
  if (articleType.value === 'news' && !item.value.type) {
    alert('종류는 필수입니다.');
    return false;
  }

  if (!item.value.title) {
    alert('제목은 필수입니다.');
    return false;
  }

  if (!item.value.periodFrom) {
    alert('시작일은 필수입니다.');
    return false;
  }

  if (!item.value.periodTo) {
    alert('종료일은 필수입니다.');
    return false;
  }

  // if (!item.value.location) {
  //   alert('장소는 필수입니다.');
  //   return false;
  // }

  if (!item.value.content) {
    alert('내용은 필수입니다.');
    return false;
  }

  // if (!item.value.contactPhoneNum) {
  //   alert('연락처는 필수입니다.');
  //   return false;
  // }

  if (!item.value.imageUrl) {
    alert('이미지는 필수입니다.');
    return false;
  }

  if (item.value.isHidden === null) {
    alert('공개여부는 필수입니다.');
    return false;
  }

  // 좌표는 선택 입력 — 비어 있어도 통과한다 (지도는 등록 요건이 아니다)
  if (articleType.value === 'exhibitions' && !validateCoordinates()) {
    return false;
  }

  return true;
}

onMounted(() => {
  if (articleUuid.value) {
    fetchItem();
  }
  if (articleType.value === 'exhibitions') {
    fetchLocationOptions();
  }
});
</script>

<style scoped>
.loc-suggest {
  position: absolute;
  z-index: 30;
  left: 0;
  right: 0;
  margin: 2px 0 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 240px;
  overflow-y: auto;
}

.loc-suggest__item {
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
}

.loc-suggest__item:hover {
  background: #f2f4f7;
}

/* 좌표 입력 (mimio#32 3a) */
.coord-inputs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/*
 * 폭·display 는 전역 규칙을 이겨야 한다.
 *   .table-wrap .table-type2 tbody tr td input[type="text"] { width: 100% }  (0,3,4)
 *   label { display: block }
 * 이 두 개 때문에 그냥 .coord-inputs input 으로 쓰면 위도·경도가 각각 전체폭으로 늘어나 4줄이 된다.
 * !important 대신 같은 경로를 앞에 붙여 특이도로 이긴다.
 */
.table-wrap .table-type2 tbody tr td .coord-inputs label {
  display: inline-block;
  font-size: 13px;
  color: #666;
}

.table-wrap .table-type2 tbody tr td .coord-inputs input[type="text"] {
  width: 170px;
}

.coord-badge {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 10px;
  white-space: nowrap;
}

.coord-badge--ok {
  background: #e8f5e9;
  color: #2e7d32;
}

.coord-badge--none {
  background: #fdecea;
  color: #c0392b;
}

.coord-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.coord-actions button {
  padding: 5px 12px;
  font-size: 13px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.coord-actions button:hover:not(:disabled) {
  background: #f2f4f7;
}

.coord-actions button:disabled {
  color: #bbb;
  border-color: #e5e5e5;
  cursor: not-allowed;
}

.coord-actions__pick {
  border-color: #1f2937;
  color: #1f2937;
  font-weight: 600;
}

.coord-actions__bulk {
  margin-left: auto;
}

.coord-hint {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
  line-height: 1.6;
}
</style>